/* SYSTEM FIELDS (locked contract B) — closed list per type: `id` (all
 * types); source docs additionally `source.id`, `capabilities[].id` and
 * `capabilities[].origin`. The EDITOR strips these for display and
 * re-injects SERVER truth on save — never values from the edited text.
 * The engine guards the same list server-side (PUT silently overwrites
 * client values), so this is belt-and-braces by design.
 * GET /defs/{type}/{id} stays the full truth — strip is display-only. */
import type {
    DefType
} from '../api/types';

export interface StripResult {
    doc: any;
    /** True when at least one system field was removed (drives the
     * "system fields are managed automatically" paste note). */
    stripped: boolean;
}

/** Display projection: the doc minus every system field. Pure. */
export function stripSystemFields(doc: any, type: DefType): StripResult {
    if (!doc || typeof doc !== 'object' || Array.isArray(doc)) {
        return {
            doc,
            stripped: false
        };
    }
    let stripped = false;
    const out = {
        ...doc
    };
    if ('id' in out) {
        delete out.id;
        stripped = true;
    }
    if (type === 'source') {
        if (out.source && typeof out.source === 'object') {
            out.source = {
                ...out.source
            };
            if ('id' in out.source) {
                delete out.source.id;
                stripped = true;
            }
        }
        if (Array.isArray(out.capabilities)) {
            out.capabilities = out.capabilities.map((cap: any) => {
                if (!cap || typeof cap !== 'object') return cap;
                if (!('id' in cap) && !('origin' in cap)) return cap;
                stripped = true;
                const c = {
                    ...cap
                };
                delete c.id;
                delete c.origin;
                return c;
            });
        }
    }
    return {
        doc: out,
        stripped
    };
}

/** SAVE projection: the edited doc with system fields re-injected from
 * the SERVER doc (the loaded truth). Source capabilities are matched by
 * NAME (each server cap consumed once); unmatched capabilities go out
 * WITHOUT id/origin — the engine mints cap_<12hex> + origin manual. */
export function reinjectSystemFields(edited: any, server: any,
    type: DefType): any {
    if (!edited || typeof edited !== 'object' || Array.isArray(edited)) {
        return edited;
    }
    const out = {
        ...edited
    };
    const srv = server && typeof server === 'object' ? server : {};
    if (typeof srv.id === 'string' && srv.id) out.id = srv.id;
    else delete out.id;
    if (type === 'source') {
        const srvSourceId = srv.source && typeof srv.source === 'object'
            ? srv.source.id : undefined;
        if (out.source && typeof out.source === 'object') {
            out.source = {
                ...out.source
            };
            if (typeof srvSourceId === 'string' && srvSourceId) {
                out.source.id = srvSourceId;
            } else {
                delete out.source.id;
            }
        }
        if (Array.isArray(out.capabilities)) {
            const pool: any[] = Array.isArray(srv.capabilities)
                ? [...srv.capabilities] : [];
            out.capabilities = out.capabilities.map((cap: any) => {
                if (!cap || typeof cap !== 'object') return cap;
                const c = {
                    ...cap
                };
                delete c.id;
                delete c.origin;
                const name = typeof c.name === 'string' ? c.name : null;
                const i = name == null ? -1 : pool.findIndex(
                    (s) => s && typeof s === 'object' && s.name === name);
                if (i >= 0) {
                    const match = pool.splice(i, 1)[0];
                    if (match.id != null) c.id = match.id;
                    if (match.origin != null) c.origin = match.origin;
                }
                return c;
            });
        }
    }
    return out;
}
